import React, { createRef, useState } from "react";
import PuiTextbox from "../../../components/elements/PuiTextbox";
import PuiTypography from "../../../components/elements/PuiTypography";
import DraggableList from "../../../components/widgets/DraggableList";
import { Group } from "../../../user/User";
import { setMessageArgs } from "../../../utils/LogicUtils";
import { MSG_INPUT_OVER_MAXLENGTH, MSG_INPUT_REQUEST_INPUT } from "../../../utils/Messages";
import { isEmpty } from "../../../utils/ObjectUtils";
import { countCharLength, validateRequired } from "../../../utils/ValidationUtils";
import styles from "./EditGroupDetail.module.css";


interface EditGroupDetailProps {
    group: any | undefined;
    updateList: Group[];
    setUpdateList: (groups: Group[]) => void;
    setInputFinished: (finished: boolean) => void;
    setIsSaved: (isSaved: boolean) => void;
    handleEditGroup: (title: string) => void;
}

export default function EditGroupDetail({group, updateList, setUpdateList, setInputFinished, setIsSaved, handleEditGroup}: EditGroupDetailProps) {

    // 定数
    const MAXLEN_TITLE = 40;
    const MAXLEN_DESCRIPTION = 400;

    // パラメータ初期化
    const titleRef = createRef<HTMLInputElement>();
    const descriptionRef = createRef<HTMLInputElement>();
    const [titleError, setTitleError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");
    // eslint-disable-next-line
    const [validInput, setValidInput] = useState<boolean>(true);

    // 入力チェック（必須、桁数）
    const checkInput = () => {
        if (validateRequired(titleRef.current?.value, descriptionRef.current?.value)
            && countCharLength(titleRef.current?.value) <= MAXLEN_TITLE
            && countCharLength(descriptionRef.current?.value) <= MAXLEN_DESCRIPTION
        ) {
            setValidInput(true);
            setTitleError("");
            setDescriptionError("");
            setInputFinished(true);
            return true;
        } else {
            setValidInput(false);
            setInputFinished(false);
            return false;
        }
    };

    // タイトル編集イベント
    const handleInputTitle = () => {
        checkInput();
        countCharLength(titleRef.current?.value) > MAXLEN_TITLE ? setTitleError(MSG_INPUT_OVER_MAXLENGTH) : setTitleError("");
        if (group) {
            group["title"] = String(titleRef.current?.value);
            let newList = updateList.filter((item) => item.id !== group.id);
            setUpdateList([...newList, group]);
            setIsSaved(false);

            handleEditGroup(String(titleRef.current?.value));
        }
    };

    // 概要編集イベント
    const handleInputDescription = () => {
        checkInput();
        countCharLength(descriptionRef.current?.value) > MAXLEN_DESCRIPTION ? setDescriptionError(MSG_INPUT_OVER_MAXLENGTH) : setDescriptionError("");
        if (group) {
            group["description"] = String(descriptionRef.current?.value);
            let newList = updateList.filter((item) => item.id !== group.id);
            setUpdateList([...newList, group]);
            setIsSaved(false);

            handleEditGroup(String(titleRef.current?.value));
        }
    };

    // ユーザーリスト編集イベント
    const handleChangeUserList = (targetId: string, targetList: any) => {
        if (group) {
            group["userList"] = targetList;
            let newList = updateList.filter((item) => item.id !== group.id);
            setUpdateList([...newList, group]);
            setIsSaved(false);

            handleEditGroup(String(titleRef.current?.value));
        }
    };
    // ビデオリスト編集イベント
    const handleChangeVideoList = (targetId: string, targetList: any) => {
        if (group) {
            group["videoList"] = targetList;
            let newList = updateList.filter((item) => item.id !== group.id);
            setUpdateList([...newList, group]);
            setIsSaved(false);

            handleEditGroup(String(titleRef.current?.value));
        }
    };

    // ドキュメントリスト編集イベント
    const handleChangeSlidestockList = (targetId: string, targetList: any) => {
        if (group) {
            group["slideList"] = targetList;
            let newList = updateList.filter((item) => item.id !== group.id);
            setUpdateList([...newList, group]);
            setIsSaved(false);

            handleEditGroup(String(titleRef.current?.value));
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.editor}>
                <div>
                    <PuiTypography size={20} bold={true} color="black">
                        Group名
                    </PuiTypography>
                    <div className={styles.title}>
                        <PuiTextbox 
                            id="title" 
                            ref={titleRef} 
                            children={group?.title}
                            placeholder={setMessageArgs(MSG_INPUT_REQUEST_INPUT, "Groupタイトル")}
                            error={!isEmpty(titleError)}
                            helperText={setMessageArgs(titleError, "20", "40")}
                            onChange={handleInputTitle} 
                        />
                    </div>
                </div>
                <div>
                    <PuiTypography size={20} bold={true} color="black">
                        Group説明
                    </PuiTypography>
                    <div className={styles.description}>
                        <PuiTextbox 
                            id="description" 
                            ref={descriptionRef} 
                            children={group?.description}
                            placeholder={setMessageArgs(MSG_INPUT_REQUEST_INPUT, "Group説明")} 
                            multiline={true} 
                            rows={3} 
                            error={!isEmpty(descriptionError)}
                            helperText={setMessageArgs(descriptionError, "200", "400")}
                            onChange={handleInputDescription} 
                        />
                    </div>
                </div>
                <div>
                    <PuiTypography size={20} bold={true} color="black">
                        Group User
                    </PuiTypography>
                    <div className={styles.userList}>
                        {group && group.userList && group.userList.length > 0 && (
                            <DraggableList 
                                type="userList" 
                                targetList={group.userList}
                                onUpdate={handleChangeUserList}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className={styles.contentArea}>
                <div className={styles.videoArea}>
                    <div className={styles.titleArea}>
                        <PuiTypography size={20} bold={true} color="black">
                            Group Video
                        </PuiTypography>
                    </div>
                    <div className={styles.videoList}>
                        {group && group.videoList && group.videoList.length > 0 && (
                            <DraggableList
                                type="slideList"
                                targetList={group.videoList}
                                onUpdate={handleChangeVideoList}
                            />
                        )}
                    </div>
                </div>
                <div className={styles.videoArea}>
                    <div className={styles.titleArea}>
                        <PuiTypography size={20} bold={true} color="black">
                            Group Slide
                        </PuiTypography>
                    </div>
                    <div className={styles.videoList}>
                        {group && group.slideList && group.slideList.length > 0 && (
                            <DraggableList
                                type="videoList"
                                targetList={group.slideList}
                                onUpdate={handleChangeSlidestockList}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
